import { useStoredState } from '../storage'
import type { Cliente, Contrato, Ruta, Venta } from '../types'

// Estado compartido de los 4 módulos del sistema de gas industrial.
// Usa el mismo mecanismo que el resto de la app (localStorage + Supabase
// vía useStoredState), así que persiste y queda listo para engancharse a
// tablas reales de Supabase más adelante sin tocar los componentes.

export function useGasData() {
  const [clientes, setClientes] = useStoredState<Cliente[]>('gas.clientes', [])
  const [contratos, setContratos] = useStoredState<Contrato[]>(
    'gas.contratos',
    [],
  )
  const [rutas, setRutas] = useStoredState<Ruta[]>('gas.rutas', [])
  const [ventas, setVentas] = useStoredState<Venta[]>('gas.ventas', [])

  // ── Clientes ──────────────────────────────────────────────────────
  const onAddCliente = (c: Cliente) => setClientes((prev) => [...prev, c])
  const onUpdateCliente = (c: Cliente) =>
    setClientes((prev) => prev.map((x) => (x.id === c.id ? c : x)))
  const onDeleteCliente = (id: string) =>
    setClientes((prev) => prev.filter((x) => x.id !== id))

  // ── Contratos ─────────────────────────────────────────────────────
  const onAddContrato = (c: Contrato) => setContratos((prev) => [...prev, c])
  const onUpdateContrato = (c: Contrato) =>
    setContratos((prev) => prev.map((x) => (x.id === c.id ? c : x)))
  const onDeleteContrato = (id: string) =>
    setContratos((prev) => prev.filter((x) => x.id !== id))

  // ── Rutas ─────────────────────────────────────────────────────────
  const onAddRuta = (r: Ruta) => setRutas((prev) => [...prev, r])
  const onUpdateRuta = (r: Ruta) =>
    setRutas((prev) => prev.map((x) => (x.id === r.id ? r : x)))
  const onDeleteRuta = (id: string) =>
    setRutas((prev) => prev.filter((x) => x.id !== id))

  // ── Ventas ────────────────────────────────────────────────────────
  // Al registrar una venta se actualiza automáticamente "última compra"
  // del cliente correspondiente, para que Clientes/Dashboard reflejen el
  // estado real sin tener que recalcularlo a mano en cada módulo.
  const onAddVenta = (v: Venta) => {
    setVentas((prev) => [...prev, v])
    setClientes((prev) =>
      prev.map((c) =>
        c.id === v.clienteId ? { ...c, ultima_compra: v.fecha } : c,
      ),
    )
  }
  const onUpdateVenta = (v: Venta) =>
    setVentas((prev) => prev.map((x) => (x.id === v.id ? v : x)))
  const onDeleteVenta = (id: string) =>
    setVentas((prev) => prev.filter((x) => x.id !== id))

  return {
    clientes,
    contratos,
    rutas,
    ventas,
    onAddCliente,
    onUpdateCliente,
    onDeleteCliente,
    onAddContrato,
    onUpdateContrato,
    onDeleteContrato,
    onAddRuta,
    onUpdateRuta,
    onDeleteRuta,
    onAddVenta,
    onUpdateVenta,
    onDeleteVenta,
  }
}
